import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Calendar, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useState } from 'react';
import SEO from '../../components/SEO';

export default function ServiceTemplate(props) {
  const [openFaq, setOpenFaq] = useState(null);
  
  const { title, tagline, description, causesTitle, causes, symptoms, treatments, faqs, cta, seoTitle, seoDescription, seoKeywords, seoCanonical } = props;

  return (
    <div className="pb-20 md:pb-0">
      <SEO
        title={seoTitle || title}
        description={seoDescription || description}
        keywords={seoKeywords}
        canonical={seoCanonical}
      />
      <div className="bg-slate-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center text-sm text-slate-500">
            <Link to="/" className="hover:text-black">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/mens-health" className="hover:text-black">Services</Link>
            <span className="mx-2">/</span>
            <span className="text-primary">{title}</span>
          </nav>
        </div>
      </div>

      <section className="py-12 md:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-black font-medium mb-2">{tagline}</p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-6">{title}</h1>
            <p className="text-lg text-slate-600 leading-relaxed">{description}</p>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading text-2xl font-bold text-primary mb-6">{causesTitle}</h2>
              <ul className="space-y-3">
                {causes.map((item, idx) => (
                  <li key={idx} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-black flex-shrink-0 mr-3 mt-0.5" />
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            {symptoms && symptoms.length > 0 && (
              <div>
                <h2 className="font-heading text-2xl font-bold text-primary mb-6">Symptoms</h2>
                <ul className="space-y-3">
                  {symptoms.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-black flex-shrink-0 mr-3 mt-0.5" />
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl font-bold text-primary mb-8">Treatment Options</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {treatments.map((t, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl border border-slate-100">
                <div className="w-10 h-10 bg-black/10 rounded-full flex items-center justify-center mb-4">
                  <span className="font-bold">{idx + 1}</span>
                </div>
                <p className="text-slate-700 font-medium">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {faqs && faqs.length > 0 && (
        <section className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-2xl font-bold text-primary mb-8">FAQs</h2>
            <div className="space-y-4">
              {faqs.map((f, idx) => (
                <div key={idx} className="bg-slate-50 rounded-xl border border-slate-100">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between p-5 text-left">
                    <span className="font-semibold text-primary pr-4">{f.q}</span>
                    <ChevronDown className={`h-5 w-5 text-slate-400 ${openFaq === idx ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === idx && <div className="px-5 pb-5 text-slate-600">{f.a}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-12 md:py-16 bg-black text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-gray-300 mb-8">{cta}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-black hover:bg-gray-200 rounded-full px-8">
              <Link to="/contact">Request Appointment</Link>
            </Button>
            <a href="tel:+13215004545" className="inline-flex items-center justify-center px-8 py-3 border-2 border-white text-white rounded-full font-medium hover:bg-white/10">
              <Phone className="h-5 w-5 mr-2" />(321) 500-4545
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
